import * as React from 'react';
import {
    Alert,
    AlertTitle,
    Box,
    Button,
    Checkbox,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControlLabel,
    IconButton,
    Paper,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';

export interface ReviewRow {
    id: string | number;
    line?: number;
    label: string;
    detail?: string;
    score?: number;
    /** false => row is blocked (low-confidence match / missing data): shown but not selectable. */
    valid: boolean;
    reason?: string;
}

export interface ImportReviewDialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    /** Short explanation of what approving will do, shown above the table. */
    description?: string;
    rows: ReviewRow[];
    entityLabel?: string;
    /** Called with the ids the user approved; the caller performs the actual mutation. */
    onApprove: (selectedIds: Set<string | number>) => void | Promise<void>;
}

/**
 * Generic "preview before you commit" gate for the bespoke spreadsheet importers (orders,
 * inventory, delivery certificates, distribution amounts) that don't fit the full entity
 * create/update ImportWizardDialog but still need a real checkbox review step before rows
 * are merged into local state or sent to the server - not a report shown after the fact.
 */
export default function ImportReviewDialog({ open, onClose, title, description, rows, entityLabel, onApprove }: ImportReviewDialogProps) {
    const [selected, setSelected] = React.useState<Set<string | number>>(new Set());
    const [approved, setApproved] = React.useState(false);
    const [submitting, setSubmitting] = React.useState(false);

    React.useEffect(() => {
        if (open) {
            setSelected(new Set(rows.filter((r) => r.valid).map((r) => r.id)));
            setApproved(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, rows]);

    const validCount = rows.filter((r) => r.valid).length;
    const blockedCount = rows.length - validCount;
    const selectedCount = selected.size;

    const toggle = (id: string | number) => {
        setSelected((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const setAll = (value: boolean) => {
        setSelected(value ? new Set(rows.filter((r) => r.valid).map((r) => r.id)) : new Set());
    };

    const handleApprove = async () => {
        setSubmitting(true);
        try {
            await onApprove(selected);
        } finally {
            setSubmitting(false);
            onClose();
        }
    };

    return (
        <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>{title}</span>
                <IconButton onClick={onClose} disabled={submitting} size="small">
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {description ? (
                    <Alert severity="info" sx={{ mb: 2 }}>
                        {description}
                    </Alert>
                ) : null}

                {blockedCount > 0 ? (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <AlertTitle>שימו לב</AlertTitle>
                        {blockedCount} שורות לא נחסמו לתיקון ידני: לא נמצאה התאמה מלאה בטוחה, ולכן לא ניתן לסמן אותן. ניתן לתקן את הקובץ ולהעלות מחדש.
                    </Alert>
                ) : null}

                <Stack direction="row" spacing={1} sx={{ mb: 1 }}>
                    <Button size="small" onClick={() => setAll(true)}>
                        בחר את כל השורות התקינות
                    </Button>
                    <Button size="small" onClick={() => setAll(false)}>
                        נקה בחירה
                    </Button>
                </Stack>

                <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox" />
                                {rows.some((r) => r.line != null) ? <TableCell>שורה</TableCell> : null}
                                <TableCell>{entityLabel || 'פרט'}</TableCell>
                                <TableCell>פרטים</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows.map((r) => (
                                <TableRow key={r.id} hover>
                                    <TableCell padding="checkbox">
                                        <Checkbox checked={selected.has(r.id)} disabled={!r.valid} onChange={() => toggle(r.id)} />
                                    </TableCell>
                                    {rows.some((x) => x.line != null) ? <TableCell>{r.line ?? ''}</TableCell> : null}
                                    <TableCell>
                                        {r.label}
                                        {r.score != null ? (
                                            <Chip
                                                size="small"
                                                sx={{ ml: 1 }}
                                                color={r.valid ? 'success' : 'default'}
                                                label={`התאמה ${Math.round(r.score * 100)}%`}
                                            />
                                        ) : null}
                                    </TableCell>
                                    <TableCell>
                                        {r.detail ? <Typography variant="caption">{r.detail}</Typography> : null}
                                        {!r.valid && r.reason ? (
                                            <Typography variant="caption" color="error" display="block">
                                                {r.reason}
                                            </Typography>
                                        ) : null}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>

                <Box sx={{ mt: 2 }}>
                    <Alert severity={selectedCount === 0 ? 'info' : 'success'}>
                        {selectedCount} מתוך {rows.length} שורות מסומנות לביצוע.
                    </Alert>
                    <FormControlLabel
                        sx={{ mt: 1 }}
                        control={<Checkbox checked={approved} onChange={(e) => setApproved(e.target.checked)} disabled={selectedCount === 0} />}
                        label="אני מאשר לבצע את השינויים המסומנים בלבד"
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} disabled={submitting}>
                    ביטול
                </Button>
                <Button
                    variant="contained"
                    color="success"
                    startIcon={selectedCount > 0 ? <CheckCircleIcon /> : <ErrorIcon />}
                    disabled={!approved || selectedCount === 0 || submitting}
                    onClick={handleApprove}
                >
                    ביצוע ({selectedCount})
                </Button>
            </DialogActions>
        </Dialog>
    );
}
